import type { Route } from 'vue-router';
import type { OAuthProvider, OAuthType } from './types';
import { OAUTH_URL } from './constants';
import {
  buildQuery,
  convertQueryOrParamsToString,
  getUtmParams,
  isExternalReferer,
} from '~/components/utils/browser/url';

const allowedExternalDomains = ['bravado.xperiencify.io', 'indigo-autonomy-951063.framer.app'];

/**
 * Referer must be relative path or landing on bravado.co,
 * otherwise user could be redirected to phishing site just by ?referer in url
 */
export const getValidReferrer = (url: string | undefined | null) => {
  if (!url) return;

  if (url.startsWith('http')) {
    const hostname = new URL(url).hostname;
    if (allowedExternalDomains.includes(hostname) || hostname.includes('bravado.co')) return url;
    return;
  }

  if (url.startsWith('/')) return url;

  return `/${url}`;
};

export const buildOAuthUrl = (
  provider: OAuthProvider,
  type: OAuthType,
  route: Route,
  extraParams: Record<string, unknown> = {},
): string => {
  const baseUrl = OAUTH_URL[provider][type];
  const { dynamic_modal, ...queryParams } = route.query;
  const redirectPath = encodeURIComponent(`${process.env.internalBaseUrl}${route.path}`);

  const query = {
    fail_refferer: String(process.client && redirectPath),
    refferer: getValidReferrer((queryParams.refferer || queryParams.referer) as string) || redirectPath,
    ...queryParams,
    // We could pass `auth_source` to save the entry point of signup to cookie on server
    ...extraParams,
  };

  /**
   * If user landed to signup from external domain we should send an event.
   * But we can't do that after oauth redirect to success page on external domain.
   * So we rewrite refferer to /redirect which log the event and redirect to
   * previous refferer
   */
  const isLoginExternal = route.path === '/login-external';
  if (isLoginExternal) {
    const event = convertQueryOrParamsToString(route.query, 'event');
    const params = {
      ...(event && { event }),
      ...getUtmParams(route.query),
      // We have a mistake in name `refferer` on backed
      referer: String(query.refferer),
    };

    // we should use the redirect page for landing pages
    // event logging
    if (isExternalReferer(params.referer)) {
      query.refferer = encodeURIComponent(`${process.env.internalBaseUrl}/redirect?${buildQuery(params)}`);

      // we can redirect direcly to our specific internal page if refferer/referer is defined
    } else if (queryParams.refferer || queryParams.referer) {
      // we can delete the refferer from the new url
      delete queryParams.refferer;
      delete queryParams.referer;

      // the new refferer url, pointing to our internal page needs to have all query params
      const queryParam = buildQuery({
        ...(event && { event }),
        ...getUtmParams(route.query),
        ...queryParams,
      });

      query.refferer = `${process.env.internalBaseUrl}${query.refferer}${queryParam ? '?' : ''}${queryParam}`;
    } else {
      // we don't want a duplicated referer in query param
      // @ts-ignore
      delete params.referer;
      // @ts-ignore
      delete params.refferer;
      query.refferer = `${process.env.internalBaseUrl}?${buildQuery(params)}`;
    }
  }

  return `${baseUrl}?${buildQuery(query)}`;
};
